import _ from 'lodash';
import MobileBasicButton from 'components/Buttons/MobileBasicButton';
import { MobileBasicInputWithLabel } from 'components/Input/BasicInput';
import { governmentReadyPageConstants } from 'constants/Government/governmentReadyPageConstants';
import { commonErrText } from 'constants/commonConstants';
import { preciousChildPaths } from 'constants/path';
import { ValueType } from 'models/Common/commonTypes';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import styled from 'styled-components';
import { AgreeAndCodeConfirmTitle, TitleContainer } from 'styles/Global/Government/agreeAndCodeConfirmPageStyles';
import { BasicInputLayout, InputWrapper, MobileInputLabel } from 'styles/Input/inputStyle';
import ArrowLineRight from 'assets/Icons/Arrow/ArrowLineRight';
import { validateCheck } from 'utils/validationFunctions';
import { DiagnosisStatus, VoucherCodeStatus, VoucherCodeType } from 'utils/statusCode';
import { changeDiagnosisStatus, mindupAddDiagnosisApiCall, updateVoucherCodeStatus } from 'functions/apiFunctions';
import { keyController } from 'utils/keyboardUtils';
import { ClientInfoSummaryModal } from './Components';
import useToggle from 'hooks/CustomHooks/useToggle';
import { mindUpAuthApi } from 'api/MindUp/mindUpAuthApi';

export const relationType = {
    mother: '0',
    father: '1',
    etc: '2',
} as const;

const InputParentInfoPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const voucherInfo = state?.data?.voucher ?? '';
    const childInputInfo = state.value;

    const errorInitValue = { parentName: '', patientMobile: '', relation: '' };

    // 공백 포함 빈값 판정
    const isBlank = (s?: string) => !s || s.trim().length === 0;

    const [value, setValue] = useState<ValueType>({
        patientName: childInputInfo.patientName,
        birthDate: `${childInputInfo.birthYear}${childInputInfo.birthMonth}${childInputInfo.birthDay}`,
        gender: childInputInfo.gender,
        schoolAge: childInputInfo.schoolAge,
        semester: childInputInfo.semester,
        grade: childInputInfo.grade,
        voucherCode: voucherInfo?.code ?? '',
        parentName: '',
        patientMobile: '',
        relationType: '0',
    });

    const [errMsg, setErrMsg] = useState<ValueType>(errorInitValue);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [isShowSubmitAlert, setIsShowSubmitAlert] = useToggle();

    const { setHeaderText } = useGovernmentHeaderStore();

    const parentNameInputRef = useRef<HTMLInputElement>(null);
    const patientMobileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setHeaderText(governmentReadyPageConstants.headerText);
    }, []);

    // Next 버튼 활성화 체크 (trim 기반)
    useEffect(() => {
        const allFilled = value.relationType !== '' && !isBlank(value.parentName) && !isBlank(value.patientMobile);

        if (!allFilled) {
            setButtonDisabled(true);
            return;
        }

        const hasError = _.findIndex([errMsg.parentName, errMsg.patientMobile], (e) => !_.isEmpty(e)) > -1;

        setButtonDisabled(hasError);
    }, [value, errMsg]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, id: string) => {
        keyController(e, id, { parentNameInputRef, patientMobileInputRef });
    };

    // input change handler (trim 안전 처리)
    const handleInputChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>, id: string) => {
        if (id === 'patientMobile') {
            const onlyNum = e.target.value.replace(/[^0-9]/g, '');
            setValue({ ...value, [id]: onlyNum });

            const err = validateCheck.phoneNumber(onlyNum);
            setErrMsg((prev) => ({ ...prev, [id]: err }));
            return;
        }

        if (id === 'error') return;

        if (id === 'parentName') {
            const raw = e.target.value;
            const next = raw.length > 20 && !isBlank(raw) ? raw.slice(0, 20) : raw;
            setValue({ ...value, parentName: next });

            if (isBlank(next)) {
                setErrMsg((prev) => ({ ...prev, parentName: commonErrText.required }));
                return;
            }
            setErrMsg((prev) => ({ ...prev, parentName: '' }));
            return;
        }

        // 기타 필드 (현재 없음)
        setValue({ ...value, [id]: e.target.value });
        setErrMsg((prev) => ({ ...prev, [id]: '' }));
    };

    const handleRealtionChangeButton = (relation: string) => {
        setValue({ ...value, relationType: relation });
    };

    // 진행 전 최종 검증 (trim 기반)
    const validateBeforeProceed = () => {
        if (isBlank(value.parentName) || isBlank(value.patientMobile) || !value.relationType) {
            if (isBlank(value.parentName)) {
                setErrMsg((prev) => ({ ...prev, parentName: commonErrText.required }));
                parentNameInputRef.current?.focus();
            } else if (isBlank(value.patientMobile)) {
                setErrMsg((prev) => ({ ...prev, patientMobile: commonErrText.required }));
                window.scrollTo(0, 0);
                patientMobileInputRef.current?.focus();
            }
            return false;
        }

        // relation 제외 에러 존재 여부
        const hasError = Object.entries(errMsg).some(([key, error]) => key !== 'relation' && error !== '');
        if (hasError) {
            if (errMsg.parentName) {
                parentNameInputRef.current?.focus();
            } else if (errMsg.patientMobile) {
                window.scrollTo(0, 0);
                patientMobileInputRef.current?.focus();
            }
            return false;
        }

        return true;
    };

    const clickButton = async () => {
        if (!validateBeforeProceed()) return;

        const goReasonPage = () => {
            navigate(preciousChildPaths.counselReason, {
                replace: false,
                state: {
                    value: {
                        ...value,
                        relationType: Number(value.relationType),
                    },
                    data: state.data,
                },
            });
        };

        const openSummaryModal = () => {
            if (!isShowSubmitAlert) setIsShowSubmitAlert();
        };

        if (_.isEmpty(voucherInfo) || voucherInfo.type === VoucherCodeType.MINDUPAI) {
            openSummaryModal();
        } else {
            goReasonPage();
        }
    };

    const addDiagnosis = async () => {
        try {
            const hospitalId = state?.data?.hospitalId ?? '677e22fe3d73c33a066fc54a';
            const token = state?.data?.token ?? state.data;
            const diagnosisData = await mindupAddDiagnosisApiCall(value, token, hospitalId);

            await changeDiagnosisStatus(diagnosisData._id, DiagnosisStatus.inProgress, token);

            const voucherCode = _.isEmpty(voucherInfo) ? diagnosisData.voucherCode : value.voucherCode;

            if (voucherCode === undefined) return;

            await updateVoucherCodeStatus(voucherCode, VoucherCodeStatus.USED, token);

            const voucherModel = state?.data?.voucher ?? (await mindUpAuthApi.verifyPurchaseCode({ code: voucherCode }));

            navigate(preciousChildPaths.inspectionGuide, {
                replace: true,
                state: { token: token, diagnosis: diagnosisData, voucher: voucherModel },
            });
        } catch (error) {
            console.error('API 호출 실패:', error);
        }
    };

    return (
        <Layout>
            <ClientInfoSummaryModal
                isOpen={isShowSubmitAlert}
                onClose={setIsShowSubmitAlert}
                onSubmit={() => {
                    addDiagnosis();
                    setIsShowSubmitAlert();
                }}
                clientInfo={value}
            />
            <TitleAndWarningContainer>
                <TitleContainer>
                    <AgreeAndCodeConfirmTitle>주양육자의 정보를</AgreeAndCodeConfirmTitle>
                    <AgreeAndCodeConfirmTitle>입력해주세요.</AgreeAndCodeConfirmTitle>
                </TitleContainer>
            </TitleAndWarningContainer>
            <BodyContainer>
                <MobileBasicInputWithLabel
                    ref={parentNameInputRef}
                    id='parentName'
                    label='주양육자 닉네임 '
                    subCaption='* 반드시 실명이 아닌 닉네임으로 적어주세요'
                    placeholder='주양육자의 닉네임'
                    focus={true}
                    onChange={handleInputChange}
                    value={value.parentName}
                    errorMsg={errMsg.parentName}
                    onKeyDown={(e) => handleKeyDown(e, 'parentName')}
                    disabled={false}
                />
                <BasicInputLayout>
                    <InputWrapper>
                        <MobileInputLabel $marginBottom='0'>자녀와의 관계</MobileInputLabel>
                    </InputWrapper>
                    <IdentityNumberInputWrapper>
                        <MobileBasicButton $type={value.relationType === relationType.mother ? 'fill' : 'outLine'} onClick={() => handleRealtionChangeButton(relationType.mother)}>
                            엄마
                        </MobileBasicButton>
                        <MobileBasicButton $type={value.relationType === relationType.father ? 'fill' : 'outLine'} onClick={() => handleRealtionChangeButton(relationType.father)}>
                            아빠
                        </MobileBasicButton>
                        <MobileBasicButton $type={value.relationType === relationType.etc ? 'fill' : 'outLine'} onClick={() => handleRealtionChangeButton(relationType.etc)}>
                            기타
                        </MobileBasicButton>
                    </IdentityNumberInputWrapper>
                </BasicInputLayout>
                <MobileBasicInputWithLabel
                    ref={patientMobileInputRef}
                    id='patientMobile'
                    label='주양육자 연락처'
                    desc='검사 후 상담을 위해 연락처를 입력해주세요.'
                    placeholder='상담 받으실 연락처 (숫자만 입력)'
                    onChange={handleInputChange}
                    value={value.patientMobile}
                    errorMsg={errMsg.patientMobile}
                />
            </BodyContainer>
            <MobileBasicButton onClick={clickButton} $type='fill' disabled={buttonDisabled}>
                {'다음 '} <ArrowLineRight />
            </MobileBasicButton>
        </Layout>
    );
};

export default InputParentInfoPage;

/* ---------------- styles ---------------- */

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0px 16px;
    gap: 32px;
`;

const IdentityNumberInputWrapper = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TitleAndWarningContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 20px;
`;

const BodyContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 36px;
`;
